import {
  ResourceFilters,
  GetResourcesRequest,
  CreateResourceClientData,
  CreateResourceRequest,
  UpdateResourceClientData,
  UpdateResourceRequest,
  SoftDeleteResourceClientData,
  SoftDeleteResourceRequest,
  RestoreResourceClientData,
  RestoreResourceRequest,
  HardDeleteResourceClientData,
  HardDeleteResourceRequest,
  BaseMutationOptions,
  ResolverMutationRequestMetadata,
  parseResolverResponse
} from '@esg/syntiro-api-core';
import { Resource } from '@esg/esg-global-types';
import { FetchResult } from '@apollo/client';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { auth } from '../../lib/google/firebase';
import { validateParams, isValidFirestoreId, isValidBoolean } from '../validators/base.validator';
import {
  isValidResourceUri,
  isValidResourceName,
  isValidResourceFileType,
  isValidResourceTags,
  isValidResourceCategories,
  isValidResourceFileSize,
  isValidResourceSource,
  isValidResourceDescription,
  isValidFileExtension,
  isValidResourceAlias
} from '../validators/resource.validator';
import { apollo_client_v2 } from '../../App';
import { GET_RESOURCES } from '../queries/resource.queries';
import {
  CREATE_RESOURCE,
  UPDATE_RESOURCE,
  SOFT_DELETE_RESOURCE,
  RESTORE_RESOURCE,
  HARD_DELETE_RESOURCE
} from '../mutations/resource.mutations';

const SOURCE = 'resource.service.ts';

/**
 * Query resources from the master list, optionally filtered.
 * Use alias_exact_match or alias_substring_match to filter by alias (replaces getResourcesByAlias).
 * @param {ResourceFilters} filters Optional filter parameters.
 * @returns {Promise<Array<Resource>>}
 */
export const getResources = async (filters: ResourceFilters = {}): Promise<Array<Resource>> => {
  const input: GetResourcesRequest['input'] = {
    metadata: {},
    filter: {
      deleted: false,
      ...filters
    }
  };
  return apollo_client_v2
    .query({
      query: GET_RESOURCES,
      variables: { input },
      fetchPolicy: 'no-cache'
    })
    .then((response: FetchResult) => {
      if (!response.data?.getResources) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('getResources'));
      }
      const result: Array<Resource> | null | undefined = parseResolverResponse<Array<Resource>>(
        response.data.getResources
      );
      return result ?? [];
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getResources'),
        { filters },
        tracking_id
      );
    });
};

/**
 * Create a resource in the master list.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {CreateResourceClientData} data Required data to populate resource fields.
 * @param {BaseMutationOptions} options Standard mutator options to pass to the API.
 * @returns {Promise<Resource | null | undefined>}
 */
export const createResource = async (
  metadata: ResolverMutationRequestMetadata,
  data: CreateResourceClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Resource | null | undefined> => {
  validateParams(
    { data },
    {
      'data.uri': { validate: isValidResourceUri, message: 'Invalid resource URI specified.' },
      'data.thumbnail_uri': {
        validate: isValidResourceUri,
        message: 'Invalid resource thumbnail URI specified.'
      },
      'data.name': { validate: isValidResourceName, message: 'Invalid resource name specified.' },
      'data.type': {
        validate: isValidResourceFileType,
        message: 'Invalid resource file type specified.'
      },
      'data.tags': { validate: isValidResourceTags, message: 'Invalid resource tags specified.' },
      'data.categories': {
        validate: isValidResourceCategories,
        message: 'Invalid resource categories specified.'
      },
      'data.size': {
        validate: isValidResourceFileSize,
        message: 'Invalid resource file size specified.'
      },
      'data.source': {
        validate: isValidResourceSource,
        message: 'Invalid resource source specified.'
      },
      'data.description': {
        validate: isValidResourceDescription,
        message: 'Invalid resource description specified.',
        is_optional: true
      },
      'data.file_extension': {
        validate: isValidFileExtension,
        message: 'Invalid resource file extension specified.'
      },
      'data.alias': {
        validate: isValidResourceAlias,
        message: 'Invalid resource alias specified.',
        is_optional: true
      },
      'data.allow_download': {
        validate: isValidBoolean,
        message: 'Invalid resource allow_download specified.',
        is_optional: true
      }
    }
  );
  const input: CreateResourceRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      resource: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: CREATE_RESOURCE,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.createResource) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('createResource'));
      }
      return parseResolverResponse<Resource>(response.data.createResource);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createResource'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Update a resource in the master list.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {UpdateResourceClientData} data Resource fields to update (must include id).
 * @param {BaseMutationOptions} options Standard mutator options to pass to the API.
 * @returns {Promise<Resource | null | undefined>}
 */
export const updateResource = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateResourceClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Resource | null | undefined> => {
  validateParams(
    { data },
    {
      'data.id': { validate: isValidFirestoreId, message: 'Invalid resource id specified.' }
    }
  );
  const input: UpdateResourceRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      resource: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: UPDATE_RESOURCE,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.updateResource) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateResource'));
      }
      return parseResolverResponse<Resource>(response.data.updateResource);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateResource'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Soft delete a resource in the master list.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {SoftDeleteResourceClientData} data Contains the id of the resource to soft delete.
 * @param {BaseMutationOptions} options Standard mutator options to pass to the API.
 * @returns {Promise<Resource | null | undefined>}
 */
export const softDeleteResource = async (
  metadata: ResolverMutationRequestMetadata,
  data: SoftDeleteResourceClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Resource | null | undefined> => {
  validateParams(
    { data },
    {
      'data.id': { validate: isValidFirestoreId, message: 'Invalid resource id specified.' }
    }
  );
  const input: SoftDeleteResourceRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      resource: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: SOFT_DELETE_RESOURCE,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.softDeleteResource) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('softDeleteResource'));
      }
      return parseResolverResponse<Resource>(response.data.softDeleteResource);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'softDeleteResource'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Restore a soft-deleted resource in the master list.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {RestoreResourceClientData} data Contains the id of the resource to restore.
 * @param {BaseMutationOptions} options Standard mutator options to pass to the API.
 * @returns {Promise<Resource | null | undefined>}
 */
export const restoreResource = async (
  metadata: ResolverMutationRequestMetadata,
  data: RestoreResourceClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Resource | null | undefined> => {
  validateParams(
    { data },
    {
      'data.id': { validate: isValidFirestoreId, message: 'Invalid resource id specified.' }
    }
  );
  const input: RestoreResourceRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      resource: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: RESTORE_RESOURCE,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.restoreResource) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('restoreResource'));
      }
      return parseResolverResponse<Resource>(response.data.restoreResource);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'restoreResource'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Hard delete a resource from the master list.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request.
 * @param {HardDeleteResourceClientData} data Contains the id of the resource to hard delete.
 * @param {BaseMutationOptions} options Standard mutator options to pass to the API.
 * @returns {Promise<Resource | null | undefined>}
 */
export const hardDeleteResource = async (
  metadata: ResolverMutationRequestMetadata,
  data: HardDeleteResourceClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Resource | null | undefined> => {
  validateParams(
    { data },
    {
      'data.id': { validate: isValidFirestoreId, message: 'Invalid resource id specified.' }
    }
  );
  const input: HardDeleteResourceRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      resource: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: HARD_DELETE_RESOURCE,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.hardDeleteResource) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('hardDeleteResource'));
      }
      return parseResolverResponse<Resource>(response.data.hardDeleteResource);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'hardDeleteResource'),
        { metadata, data },
        tracking_id
      );
    });
};
